import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Globe, Link2, Users } from 'lucide-react';
import { getAudienceReport } from '../lib/api';
import { AudiencePanel, AudiencePanelRow, AudienceReport } from '../types';

/**
 * Audience dashboard, fed by the aggregated report the ops job writes on the
 * VPS from the nginx access log. No tracking script runs on the storefront.
 *
 * Every panel is read defensively: GoAccess's JSON varies with its version
 * and with the flags the job runs with — the country panel only exists once a
 * GeoIP database is installed, for instance. A missing panel says so rather
 * than rendering an empty box that reads as "no visitors".
 */

/** GoAccess dates arrive as yyyyMMdd, which is unreadable as-is. */
function formatDay(raw: string): string {
  const m = /^(\d{4})(\d{2})(\d{2})$/.exec(raw);
  return m ? `${m[3]}/${m[2]}` : raw;
}

function rows(panel: AudiencePanel | undefined): AudiencePanelRow[] {
  return Array.isArray(panel?.data) ? (panel!.data as AudiencePanelRow[]) : [];
}

function hits(row: AudiencePanelRow): number {
  return row.hits?.count ?? 0;
}

/**
 * Countries live one level down, nested under their continent. Reporting the
 * top level alone answers "Europe", which decides nothing.
 */
function flattenCountries(panel: AudiencePanel | undefined): AudiencePanelRow[] {
  return rows(panel)
    .flatMap((row) => (row.items?.length ? row.items : [row]))
    .sort((a, b) => hits(b) - hits(a));
}

interface StatProps {
  icon: typeof Users;
  label: string;
  value: string;
  hint?: string;
}

function Stat({ icon: Icon, label, value, hint }: StatProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-2 text-gray-500">
        <Icon className="w-4 h-4" aria-hidden="true" />
        <span className="text-xs uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-2 text-2xl font-semibold text-gray-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-gray-500">{hint}</div>}
    </div>
  );
}

/**
 * Hand-rolled SVG rather than a charting dependency: the bundle is already
 * large and this is a single bar series.
 */
function DailyChart({ data }: { data: AudiencePanelRow[] }) {
  const { t } = useTranslation();
  if (data.length === 0) {
    return <p className="text-sm text-gray-500">{t('adminAnalytics.noDaily')}</p>;
  }
  const max = Math.max(...data.map(hits), 1);
  const barW = 100 / data.length;

  return (
    <svg
      viewBox="0 0 100 34"
      preserveAspectRatio="none"
      className="w-full h-40"
      role="img"
      aria-label={t('adminAnalytics.dailyChartLabel')}
    >
      {data.map((row, i) => {
        const h = (hits(row) / max) * 28;
        return (
          <rect
            key={row.data}
            x={i * barW + barW * 0.15}
            y={30 - h}
            width={barW * 0.7}
            height={h}
            className="fill-brass-dark"
          >
            <title>{`${formatDay(row.data)} — ${hits(row)}`}</title>
          </rect>
        );
      })}
      <line x1="0" y1="30" x2="100" y2="30" className="stroke-gray-300" strokeWidth="0.2" />
    </svg>
  );
}

function TopList({
  icon: Icon,
  title,
  data,
  empty,
}: {
  icon: typeof Globe;
  title: string;
  data: AudiencePanelRow[];
  empty: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h2 className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
        <Icon className="w-4 h-4" aria-hidden="true" />
        {title}
      </h2>
      {data.length === 0 ? (
        <p className="text-sm text-gray-500">{empty}</p>
      ) : (
        <ul className="space-y-1.5">
          {data.slice(0, 10).map((row) => (
            <li key={row.data} className="flex items-baseline justify-between gap-3 text-sm">
              <span className="truncate text-gray-700" dir="ltr" title={row.data}>
                {row.data}
              </span>
              <span className="shrink-0 tabular-nums text-gray-900">{hits(row)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AdminAnalyticsPage() {
  const { t } = useTranslation();
  const [report, setReport] = useState<AudienceReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getAudienceReport()
      .then((data) => {
        if (cancelled) return;
        // Mock mode resolves undefined: no plausible fake exists for this,
        // and invented numbers would be worse than none.
        setReport(data ?? null);
        if (!data) setError(t('adminAnalytics.unavailableMock'));
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const daily = useMemo(() => rows(report?.visitors), [report]);
  const pages = useMemo(() => rows(report?.requests), [report]);
  const referrers = useMemo(() => rows(report?.referring_sites), [report]);
  const countries = useMemo(() => flattenCountries(report?.geolocation), [report]);

  if (loading) {
    return (
      <section className="max-w-editorial mx-auto px-6 py-10">
        <p className="text-sm text-ink-soft">{t('adminAnalytics.loading')}</p>
      </section>
    );
  }

  return (
    <section className="max-w-editorial mx-auto px-6 py-10 space-y-6">
      {/* Theme tokens, not fixed grays: this text sits directly on the themed
          page background, which is near-black under cinematic-maison. Hard
          gray-900 renders dark-on-dark there. The cards below can keep gray
          because they carry their own white surface. */}
      <header>
        <h1 className="text-2xl font-semibold text-ink">{t('adminAnalytics.title')}</h1>
        <p className="mt-1 text-sm text-ink-soft">{t('adminAnalytics.subtitle')}</p>
      </header>

      {error && (
        <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {error}
        </div>
      )}

      {report && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Stat
              icon={Users}
              label={t('adminAnalytics.visitors')}
              value={String(report.general?.unique_visitors ?? '—')}
            />
            <Stat
              icon={BarChart3}
              label={t('adminAnalytics.pageViews')}
              value={String(report.general?.total_requests ?? '—')}
            />
            <Stat
              icon={BarChart3}
              label={t('adminAnalytics.period')}
              value={`${report.general?.start_date ?? '?'} → ${report.general?.end_date ?? '?'}`}
              hint={t('adminAnalytics.dailyRefresh')}
            />
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h2 className="text-sm font-medium text-gray-700 mb-3">
              {t('adminAnalytics.daily')}
            </h2>
            <DailyChart data={daily} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <TopList
              icon={BarChart3}
              title={t('adminAnalytics.topPages')}
              data={pages}
              empty={t('adminAnalytics.noData')}
            />
            <TopList
              icon={Link2}
              title={t('adminAnalytics.referrers')}
              data={referrers}
              empty={t('adminAnalytics.noData')}
            />
            <TopList
              icon={Globe}
              title={t('adminAnalytics.countries')}
              data={countries}
              empty={t('adminAnalytics.noCountries')}
            />
          </div>

          <p className="text-xs text-ink-soft">{t('adminAnalytics.botNote')}</p>
        </>
      )}
    </section>
  );
}
